// Pure reviewer-evidence merge, shared by the browser export (which fetches
// evidence under the caller's RLS identity) and the edge function (which
// fetches it with the service role). Fetching stays environment-specific;
// only the merge lives here.

/**
 * Returns a copy of prod_data with live reviewer evidence appended to each
 * question's `evidence` array (keyed by question-id). Existing entries are
 * cloned; questions absent from prod_data are skipped (their metadata — the
 * section/title the collector attributes by — lives only in prod_data).
 */
export function mergeReviewerEvidence(
  prodData: Record<string, unknown> | null | undefined,
  reviewerEvidence: Map<string, unknown[]>
): Record<string, unknown> | null | undefined {
  if (!prodData || reviewerEvidence.size === 0) return prodData;
  const merged: Record<string, unknown> = { ...prodData };
  for (const [questionId, items] of reviewerEvidence) {
    const entry = merged[questionId];
    if (!entry || typeof entry !== "object") continue;
    const e = entry as { evidence?: unknown };
    const existing = Array.isArray(e.evidence) ? e.evidence : [];
    // Dedup by gcs_uri/url/id so a decision-flushed copy isn't shown twice.
    const key = (x: unknown) => {
      const o = x as { gcs_uri?: string; url?: string; id?: string } | null;
      return o?.gcs_uri ?? o?.url ?? o?.id ?? JSON.stringify(x);
    };
    const seen = new Set(existing.map(key));
    const additions = items.filter((x) => !seen.has(key(x)));
    merged[questionId] = { ...e, evidence: [...existing, ...additions] };
  }
  return merged;
}
